import React, { useState, useEffect, useCallback, useMemo, forwardRef, useImperativeHandle, useRef } from 'react';
import { multiLocationService, beverageProductsService, siteInventoryService } from '../services';
import api from '../services/api';
import ProductPicker from '../components/ProductPicker';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import {
  ArrowUpRight, ArrowDownLeft, Plus, Trash2, Loader2, ChevronDown, ChevronUp,
  AlertTriangle,
} from 'lucide-react';
import { getApiError } from '@/lib/utils';

const fmtCurrency = (v) => {
  if (v == null) return '\u00A30.00';
  const n = Number(v);
  return `\u00A3${n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// ── Shared hooks for site-filtered products ──────

function useSiteProducts(siteId) {
  const [allProducts, setAllProducts] = useState([]);
  const [siteProductIds, setSiteProductIds] = useState(null);

  useEffect(() => {
    beverageProductsService.listAll({})  // iter360 — no status filter; site-inventory gate applies
      .then(d => setAllProducts(Array.isArray(d) ? d : (d?.items || d?.products || [])))
      .catch(() => {});
    if (siteId) {
      siteInventoryService.listInventory(siteId)
        .then(data => {
          const ids = new Set((data?.items || data || []).map(i => String(i.beverage_product_id || i.id)));
          setSiteProductIds(ids);
        })
        .catch(() => setSiteProductIds(new Set()));
    }
  }, [siteId]);

  const products = useMemo(() => {
    if (!siteProductIds || siteProductIds.size === 0) return allProducts;
    return allProducts.filter(p => siteProductIds.has(String(p.id)));
  }, [allProducts, siteProductIds]);

  const productMap = useMemo(() => {
    const map = {};
    allProducts.forEach(p => { map[p.id] = p; });
    return map;
  }, [allProducts]);

  return { products, productMap };
}

// ── Cellar Issues Panel ─────────────────────────────

export const CellarIssuesPanel = forwardRef(function CellarIssuesPanel({ session, parentSessionId, locationId, isOfflineMode = false }, ref) {
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [locations, setLocations] = useState([]);
  const [addingReturn, setAddingReturn] = useState(false);
  const [showManualIssue, setShowManualIssue] = useState(false);
  const [addingIssue, setAddingIssue] = useState(false);
  const { products, productMap } = useSiteProducts(session?.site_id);
  // 2026-11-04 fix — forwards the wizard's "Save & Next" step-save call
  // straight through to the IssuesToLocationsGrid's own validate+save+
  // issue logic; this panel renders no other saveable/postable state
  // (the manual issues/returns sections below post immediately on their
  // own "Add" action and are untouched by this pass).
  const gridRef = useRef();
  useImperativeHandle(ref, () => ({ save: () => gridRef.current?.save() }));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await multiLocationService.getLocationTransfers(locationId, parentSessionId));
    } catch {
      toast({ title: 'Error', description: 'Failed to load transfers', variant: 'destructive' });
    } finally { setLoading(false); }
  }, [locationId, parentSessionId, toast]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (parentSessionId) {
      multiLocationService.listChildSessions(parentSessionId)
        .then(children => setLocations(children.filter(c => c.location_type !== 'cellar').map(c => ({
          id: c.audit_location_id, name: c.location_name,
        }))))
        .catch(() => {});
    }
  }, [parentSessionId]);

  if (loading) return <Card><CardContent className="py-6 text-center text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading...</CardContent></Card>;

  const issuesOut = data?.issues_out || [];
  const transfersIn = data?.transfers_in || [];

  return (
    <div className="space-y-4" data-testid="cellar-issues-panel">
      {/* iter368 — #6 Calculated Issues-to-Locations grid (primary UI). */}
      <IssuesToLocationsGrid
        ref={gridRef}
        parentSessionId={parentSessionId}
        periodFrom={session?.period_from}
        periodTo={session?.period_to}
        onSaved={load}
      />
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-orange-50 rounded-lg p-3">
          <p className="text-xs text-muted-foreground flex items-center gap-1"><ArrowUpRight className="h-3 w-3 text-orange-600" />Issued to Locations</p>
          <p className="text-xl font-bold text-orange-700" data-testid="total-issued">{fmtCurrency(data?.total_issued_value || 0)}</p>
          <p className="text-[10px] text-muted-foreground">{issuesOut.length} transfer{issuesOut.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="bg-emerald-50 rounded-lg p-3">
          <p className="text-xs text-muted-foreground flex items-center gap-1"><ArrowDownLeft className="h-3 w-3 text-emerald-600" />Returns from Locations</p>
          <p className="text-xl font-bold text-emerald-700" data-testid="total-received">{fmtCurrency(data?.total_received_value || 0)}</p>
          <p className="text-[10px] text-muted-foreground">{transfersIn.length} transfer{transfersIn.length !== 1 ? 's' : ''}</p>
        </div>
      </div>

      <Card data-testid="issues-out-section">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-2"><ArrowUpRight className="h-4 w-4 text-orange-600" />Posted Issues ({issuesOut.length})</CardTitle>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setShowManualIssue(s => !s)}
                className="text-[11px] text-slate-500 hover:text-slate-800 hover:underline"
                data-testid="cellar-issues-toggle-manual"
              >
                {showManualIssue ? 'Hide manual entry' : 'Manual entry…'}
              </button>
              {showManualIssue && !addingIssue && (
                <Button size="sm" variant="outline" onClick={() => setAddingIssue(true)} data-testid="new-issue-btn">
                  <Plus className="h-3 w-3 mr-1" />Issue Stock
                </Button>
              )}
            </div>
          </div>
          {!showManualIssue && (
            <p className="text-[11px] text-muted-foreground pt-1">
              The grid above auto-allocates calculated issues. Use "Manual entry" only for off-grid one-offs (e.g. emergency transfers outside the audit period).
            </p>
          )}
        </CardHeader>
        <CardContent className="space-y-2">
          {issuesOut.map(t => <TransferEntryCard key={t.id} transfer={t} direction="out"
            sessionEditable={session?.status === 'draft' || session?.status === 'in_progress'}
            isOfflineMode={isOfflineMode}
            onChanged={load} />)}
          {showManualIssue && addingIssue && (
            <InlineTransferEntry
              parentSessionId={parentSessionId} fromLocationId={locationId}
              locations={locations} products={products} productMap={productMap}
              directionLabel="To Location" periodFrom={session?.period_from} periodTo={session?.period_to}
              siteId={session?.site_id} sessionId={session?.id}
              onCancel={() => setAddingIssue(false)} onSaved={() => { setAddingIssue(false); load(); }}
            />
          )}
          {!addingIssue && issuesOut.length === 0 && <p className="text-sm text-muted-foreground text-center py-2">No issues posted yet — save the grid above to auto-create them.</p>}
        </CardContent>
      </Card>

      <Card data-testid="transfers-in-section">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-2"><ArrowDownLeft className="h-4 w-4 text-emerald-600" />Returns from Locations ({transfersIn.length})</CardTitle>
            {!addingReturn && <Button size="sm" variant="outline" onClick={() => setAddingReturn(true)} data-testid="new-return-btn"><Plus className="h-3 w-3 mr-1" />Record Return</Button>}
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          {transfersIn.map(t => <TransferEntryCard key={t.id} transfer={t} direction="in"
            sessionEditable={session?.status === 'draft' || session?.status === 'in_progress'}
            isOfflineMode={isOfflineMode}
            onChanged={load} />)}
          {addingReturn && (
            <InlineTransferEntry
              parentSessionId={parentSessionId} toLocationId={locationId}
              locations={locations} products={products} productMap={productMap}
              directionLabel="From Location" periodFrom={session?.period_from} periodTo={session?.period_to}
              siteId={session?.site_id} sessionId={session?.id}
              onCancel={() => setAddingReturn(false)} onSaved={() => { setAddingReturn(false); load(); }}
            />
          )}
          {!addingReturn && transfersIn.length === 0 && <p className="text-sm text-muted-foreground text-center py-2">No returns recorded.</p>}
        </CardContent>
      </Card>
    </div>
  );
});

// ── Location Transfers Panel (non-cellar) ───────────

export function LocationTransfersPanel({ session, parentSessionId, locationId, isOfflineMode = false }) {
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [locations, setLocations] = useState([]);
  const [addingOut, setAddingOut] = useState(false);
  const { products, productMap } = useSiteProducts(session?.site_id);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await multiLocationService.getLocationTransfers(locationId, parentSessionId));
    } catch {
      toast({ title: 'Error', description: 'Failed to load transfers', variant: 'destructive' });
    } finally { setLoading(false); }
  }, [locationId, parentSessionId, toast]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (parentSessionId) {
      multiLocationService.listChildSessions(parentSessionId)
        .then(children => setLocations(children.filter(c => c.audit_location_id !== locationId).map(c => ({
          id: c.audit_location_id, name: c.location_name, type: c.location_type,
        }))))
        .catch(() => {});
    }
  }, [parentSessionId, locationId]);

  if (loading) return <Card><CardContent className="py-6 text-center text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading...</CardContent></Card>;

  const transfersIn = data?.transfers_in || [];
  const issuesOut = data?.issues_out || [];

  return (
    <div className="space-y-4" data-testid="location-transfers-panel">
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-emerald-50 rounded-lg p-3">
          <p className="text-xs text-muted-foreground flex items-center gap-1"><ArrowDownLeft className="h-3 w-3 text-emerald-600" />Received (Transfers In)</p>
          <p className="text-xl font-bold text-emerald-700" data-testid="transfers-in-total">{fmtCurrency(data?.total_received_value || 0)}</p>
        </div>
        <div className="bg-orange-50 rounded-lg p-3">
          <p className="text-xs text-muted-foreground flex items-center gap-1"><ArrowUpRight className="h-3 w-3 text-orange-600" />Sent (Transfers Out)</p>
          <p className="text-xl font-bold text-orange-700" data-testid="transfers-out-total">{fmtCurrency(data?.total_issued_value || 0)}</p>
        </div>
      </div>

      <Card data-testid="location-transfers-in">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2"><ArrowDownLeft className="h-4 w-4 text-emerald-600" />Transfers In ({transfersIn.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {transfersIn.length === 0 ? <p className="text-sm text-muted-foreground text-center py-2">No transfers received.</p>
            : <div className="space-y-2">{transfersIn.map(t => <TransferEntryCard key={t.id} transfer={t} direction="in" />)}</div>}
        </CardContent>
      </Card>

      <Card data-testid="location-transfers-out">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-2"><ArrowUpRight className="h-4 w-4 text-orange-600" />Transfers Out ({issuesOut.length})</CardTitle>
            {!addingOut && <Button size="sm" variant="outline" onClick={() => setAddingOut(true)} data-testid="new-outgoing-transfer-btn"><Plus className="h-3 w-3 mr-1" />Transfer Out</Button>}
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          {issuesOut.map(t => <TransferEntryCard key={t.id} transfer={t} direction="out"
            sessionEditable={session?.status === 'draft' || session?.status === 'in_progress'}
            isOfflineMode={isOfflineMode}
            onChanged={load} />)}
          {addingOut && (
            <InlineTransferEntry
              parentSessionId={parentSessionId} fromLocationId={locationId}
              locations={locations} products={products} productMap={productMap}
              directionLabel="To Location" periodFrom={session?.period_from} periodTo={session?.period_to}
              siteId={session?.site_id} sessionId={session?.id}
              onCancel={() => setAddingOut(false)} onSaved={() => { setAddingOut(false); load(); }}
            />
          )}
          {!addingOut && issuesOut.length === 0 && <p className="text-sm text-muted-foreground text-center py-2">No outgoing transfers.</p>}
        </CardContent>
      </Card>
    </div>
  );
}

// ── Transfer Entry Card ─────────────────────────────

function TransferEntryCard({ transfer, direction, sessionEditable = true, isOfflineMode = false, onChanged }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const { toast } = useToast();
  const isOut = direction === 'out';
  const counterparty = isOut ? transfer.to_location_name : transfer.from_location_name;
  // iter428 — Transfer mutations (undo / delete) are only offered
  // while the parent stocktake is still editable AND the device is
  // online (mutations must be authoritative — KPI math depends on
  // them, so we don't queue offline writes here). Auditor can come
  // back online to correct transfers. Edit is unimplemented here
  // (the inline editor would need full wiring); auditor can Undo →
  // Delete → recreate to correct a mistake.
  const canMutate = sessionEditable && !isOfflineMode;
  const canUndo = canMutate && transfer.status === 'posted';
  const canDelete = canMutate;  // both draft and posted allowed; backend enforces

  const handleUndo = async (e) => {
    e.stopPropagation();
    if (!canUndo || busy) return;
    setBusy(true);
    try {
      await multiLocationService.unpostTransfer(transfer.id);
      toast({ title: 'Transfer un-posted', description: 'You can now edit or delete it.' });
      onChanged?.();
    } catch (err) {
      toast({ title: 'Could not un-post', description: getApiError(err, 'Failed'), variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };
  const handleDelete = async (e) => {
    e.stopPropagation();
    if (!canDelete || busy) return;
    if (!window.confirm(`Delete this transfer (${transfer.lines?.length || 0} item${(transfer.lines?.length || 0) !== 1 ? 's' : ''})? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await multiLocationService.deleteTransfer(transfer.id);
      toast({ title: 'Transfer deleted' });
      onChanged?.();
    } catch (err) {
      toast({ title: 'Could not delete', description: getApiError(err, 'Failed'), variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border rounded-lg overflow-hidden" data-testid={`transfer-entry-${transfer.id}`}>
      <div className="flex items-center justify-between px-3 py-2 cursor-pointer hover:bg-slate-50/50 text-xs" onClick={() => setOpen(!open)}>
        <div className="flex items-center gap-2 min-w-0">
          {isOut ? <ArrowUpRight className="h-3 w-3 text-orange-500 shrink-0" /> : <ArrowDownLeft className="h-3 w-3 text-emerald-500 shrink-0" />}
          <span className="font-medium truncate">{isOut ? `To ${counterparty}` : `From ${counterparty}`}</span>
          {transfer.reference_number && <span className="text-muted-foreground font-mono shrink-0">({transfer.reference_number})</span>}
          {transfer.status === 'draft' && (
            <span className="text-[10px] uppercase font-semibold tracking-wide bg-amber-100 text-amber-700 px-1.5 rounded shrink-0" data-testid={`transfer-status-${transfer.id}`}>Draft</span>
          )}
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span className="text-muted-foreground">{transfer.transfer_date}</span>
          <span className="font-medium tabular-nums">{fmtCurrency(transfer.total_value)}</span>
          <span className="text-muted-foreground">{transfer.lines?.length || 0} item{(transfer.lines?.length || 0) !== 1 ? 's' : ''}</span>
          {isOfflineMode && sessionEditable && transfer.status === 'posted' && (
            <span className="text-[10px] text-amber-600 italic shrink-0" data-testid={`transfer-offline-${transfer.id}`}
              title="Reconnect to undo or delete this transfer">Offline · read-only</span>
          )}
          {canUndo && (
            <button type="button" onClick={handleUndo} disabled={busy}
              className="text-[10px] uppercase font-semibold tracking-wide text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 px-2 py-0.5 rounded border border-indigo-200 disabled:opacity-50"
              data-testid={`transfer-undo-${transfer.id}`}
              title="Un-post this transfer so you can edit or delete it">
              Undo
            </button>
          )}
          {canDelete && (
            <button type="button" onClick={handleDelete} disabled={busy}
              className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1 rounded disabled:opacity-50"
              data-testid={`transfer-delete-${transfer.id}`}
              title="Delete this transfer">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
          {open ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        </div>
      </div>
      {open && transfer.lines && (
        <div className="border-t bg-slate-50/50">
          <table className="w-full text-xs">
            <thead><tr className="bg-slate-100 text-[10px] text-muted-foreground">
              <th className="px-3 py-1 text-left">Product</th>
              <th className="px-3 py-1 text-right">Qty</th>
              <th className="px-3 py-1 text-left">Unit</th>
              <th className="px-3 py-1 text-right">Cost/Unit</th>
              <th className="px-3 py-1 text-right">Value</th>
            </tr></thead>
            <tbody>
              {transfer.lines.map((l, i) => (
                <tr key={i} className="border-b last:border-0">
                  <td className="px-3 py-1">{l.product_code && <span className="text-muted-foreground mr-1">{l.product_code}</span>}{l.product_name}</td>
                  <td className="px-3 py-1 text-right tabular-nums">{l.quantity}</td>
                  <td className="px-3 py-1">{l.unit_label || 'unit'}</td>
                  <td className="px-3 py-1 text-right tabular-nums">{l.cost_per_unit ? fmtCurrency(l.cost_per_unit) : '-'}</td>
                  <td className="px-3 py-1 text-right tabular-nums font-medium">{l.total_value ? fmtCurrency(l.total_value) : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Inline Transfer Entry ───────────────────────────

function InlineTransferEntry({ parentSessionId, fromLocationId, toLocationId, locations, products, productMap, directionLabel, periodFrom, periodTo, siteId, sessionId, onCancel, onSaved }) {
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [targetLocId, setTargetLocId] = useState('');
  const [date, setDate] = useState(periodTo || new Date().toISOString().slice(0, 10));
  const [ref, setRef] = useState('');
  const [lines, setLines] = useState([newLine()]);
  const [costCache, setCostCache] = useState({});
  // Opening stock cost map: { productId: { unit_cost, unit_label } }
  const [openingCostMap, setOpeningCostMap] = useState({});

  // iter595 — Refs mirror the latest state so the save-on-unmount
  // cleanup can read current values without depending on stale closures.
  const linesRef = React.useRef(lines);
  linesRef.current = lines;
  const targetLocIdRef = React.useRef(targetLocId);
  targetLocIdRef.current = targetLocId;
  const dateRef = React.useRef(date);
  dateRef.current = date;
  const refValueRef = React.useRef(ref);
  refValueRef.current = ref;
  // Prevent the save-on-unmount from firing when the user clicked Discard.
  const discardedRef = React.useRef(false);

  // iter596c — Unique sessionStorage key for this transfer form instance.
  // Used to persist an unsaved draft if the unmount-triggered API call fails.
  const draftKey = `abbey_transfer_draft_${parentSessionId}_${fromLocationId ?? toLocationId}`;

  // Load opening stock costs from this session for accurate costing
  useEffect(() => {
    if (!sessionId) return;
    import('../services').then(({ stocktakeSessionsService }) => {
      stocktakeSessionsService.getOpeningStock(sessionId).then(data => {
        const map = {};
        for (const item of (data?.items || [])) {
          const pid = item.beverage_product_id;
          if (pid && item.unit_cost > 0) {
            map[pid] = {
              unit_cost: item.unit_cost,
              unit_label: item.unit_label || item.base_unit || 'bottle',
            };
          }
        }
        setOpeningCostMap(map);
      }).catch(() => {});
    });
  }, [sessionId]);

  // iter596c — Restore a draft saved by the unmount cleanup if the previous
  // fire-and-forget API call failed (e.g. network loss / 500 response).
  useEffect(() => {
    try {
      const saved = sessionStorage.getItem(draftKey);
      if (!saved) return;
      const draft = JSON.parse(saved);
      if (draft?.lines?.length > 0) {
        if (draft.targetLocId) setTargetLocId(draft.targetLocId);
        if (draft.date) setDate(draft.date);
        if (draft.ref) setRef(draft.ref);
        setLines(draft.lines.map(l => ({ ...l, _key: Math.random() })));
        toast({ title: 'Transfer draft restored', description: 'Your unsaved transfer lines were recovered. Please review and save.' });
        sessionStorage.removeItem(draftKey);
      }
    } catch { /* ignore */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function newLine() {
    return { beverage_product_id: '', quantity: '', unit_label: '', cost_per_unit: '', _key: Math.random() };
  }

  const addLine = () => setLines(prev => [...prev, newLine()]);
  const removeLine = (idx) => setLines(prev => prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev);

  // Get the "outer" default unit for a product (case/bottle, not the smallest measure)
  const getDefaultUnit = (prod) => {
    if (!prod?.units?.length) return 'unit';
    // Prefer the base stock unit (typically the larger/outer unit)
    const stockUnit = prod.units.find(u => u.is_base_stock_unit);
    if (stockUnit) return stockUnit.unit_type;
    // Fallback: pick the unit with the largest conversion_to_base (the outer)
    const sorted = [...prod.units].sort((a, b) => (parseFloat(b.conversion_to_base) || 0) - (parseFloat(a.conversion_to_base) || 0));
    return sorted[0]?.unit_type || 'unit';
  };

  // Compute cost for a given unit based on the stock unit cost
  const computeUnitCost = (prod, unitType, stockCostData) => {
    if (!prod?.units?.length || !stockCostData?.cost_per_stock_unit) return null;
    const stockCost = stockCostData.cost_per_stock_unit;
    // Find stock unit conversion
    const stockUnitType = stockCostData.stock_unit_label;
    const stockUnitDef = prod.units.find(u => u.unit_type === stockUnitType) || prod.units.find(u => u.is_base_stock_unit) || prod.units[0];
    const stockConv = parseFloat(stockUnitDef?.conversion_to_base) || 1;
    // Find selected unit conversion
    const selectedUnitDef = prod.units.find(u => u.unit_type === unitType);
    const selectedConv = parseFloat(selectedUnitDef?.conversion_to_base) || stockConv;
    // Scale: cost * (selectedConv / stockConv)
    return stockCost * (selectedConv / stockConv);
  };

  // Fetch cost: prefer opening stock cost (actual held cost), fall back to WAC API
  const fetchCost = async (productId) => {
    if (costCache[productId]) return costCache[productId];

    // 1. Try opening stock cost first (the cost the session actually holds stock at)
    const osCost = openingCostMap[parseInt(productId)];
    if (osCost && osCost.unit_cost > 0) {
      const entry = { cost_per_stock_unit: osCost.unit_cost, stock_unit_label: osCost.unit_label };
      setCostCache(prev => ({ ...prev, [productId]: entry }));
      return entry;
    }

    // 2. Fall back to WAC from product-cost API
    if (!siteId) return null;
    try {
      const resp = await api.get('/stocktake/product-cost', { params: { product_id: productId, site_id: siteId, method: 'weighted_average' } });
      const data = resp.data;
      if (data?.cost_per_stock_unit > 0) {
        const entry = { cost_per_stock_unit: data.cost_per_stock_unit, stock_unit_label: data.stock_unit_label || '' };
        setCostCache(prev => ({ ...prev, [productId]: entry }));
        return entry;
      }
    } catch { /* silent */ }
    return null;
  };

  // When product changes: set default unit, fetch cost, compute unit cost
  const handleProductChange = async (idx, productId) => {
    const prod = productMap[parseInt(productId)];
    const defaultUnit = prod ? getDefaultUnit(prod) : 'unit';
    setLines(prev => prev.map((l, i) => i === idx ? { ...l, beverage_product_id: productId, unit_label: defaultUnit, cost_per_unit: '' } : l));
    if (!productId) return;
    const costData = await fetchCost(productId);
    if (costData && prod) {
      const cost = computeUnitCost(prod, defaultUnit, costData);
      if (cost != null) {
        setLines(prev => prev.map((l, i) => i === idx ? { ...l, cost_per_unit: cost.toFixed(4) } : l));
      }
    }
  };

  // When unit changes: recompute cost for the new unit
  const handleUnitChange = (idx, newUnit) => {
    const line = lines[idx];
    const prod = productMap[parseInt(line.beverage_product_id)];
    const costData = costCache[line.beverage_product_id];
    let newCost = '';
    if (prod && costData) {
      const c = computeUnitCost(prod, newUnit, costData);
      if (c != null) newCost = c.toFixed(4);
    }
    setLines(prev => prev.map((l, i) => i === idx ? { ...l, unit_label: newUnit, cost_per_unit: newCost } : l));
  };

  const updateLine = (idx, field, val) => setLines(prev => prev.map((l, i) => i === idx ? { ...l, [field]: val } : l));

  // Auto-save: when location + at least 1 valid line, auto-create after 1.5s debounce
  const autoSaveRef = React.useRef(null);
  const hasSaved = React.useRef(false);

  // iter596b — Guard against hard-refresh / tab-close while the inline
  // transfer form has valid unsaved lines. The save-on-unmount handles
  // same-domain SPA navigation; beforeunload covers hard-refresh (Ctrl+R)
  // and tab-close where no unmount cleanup fires.
  useEffect(() => {
    if (hasSaved.current) return undefined;
    const validLines = lines.filter(l => l.beverage_product_id && parseFloat(l.quantity) > 0);
    if (validLines.length === 0 || !targetLocId) return undefined;
    const handler = (e) => { e.preventDefault(); e.returnValue = ''; return ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [lines, targetLocId]);

  useEffect(() => {
    if (hasSaved.current) return;
    if (!targetLocId) return;
    const validLines = lines.filter(l => l.beverage_product_id && parseFloat(l.quantity) > 0);
    if (validLines.length === 0) return;

    if (autoSaveRef.current) clearTimeout(autoSaveRef.current);
    autoSaveRef.current = setTimeout(() => {
      doAutoSave(validLines);
    }, 1500);
    return () => { if (autoSaveRef.current) clearTimeout(autoSaveRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetLocId, lines, date, ref]);

  const doAutoSave = async (validLines) => {
    if (hasSaved.current || saving) return;
    if (periodFrom && date < periodFrom) return;
    if (periodTo && date > periodTo) return;
    setSaving(true);
    hasSaved.current = true;
    try {
      await multiLocationService.createLocationTransfer({
        parent_session_id: parentSessionId,
        from_location_id: fromLocationId || parseInt(targetLocId),
        to_location_id: toLocationId || parseInt(targetLocId),
        transfer_date: date,
        reference_number: ref || null,
        lines: validLines.map(l => ({
          beverage_product_id: parseInt(l.beverage_product_id),
          quantity: parseFloat(l.quantity),
          unit_label: l.unit_label || null,
          cost_per_unit: l.cost_per_unit ? parseFloat(l.cost_per_unit) : null,
        })),
      });
      toast({ title: 'Transfer saved' });
      onSaved();
    } catch (err) {
      hasSaved.current = false;
      toast({ title: 'Error', description: getApiError(err, 'Failed'), variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const handleDiscard = () => {
    // iter595 — Mark as discarded so save-on-unmount doesn't fire a POST.
    discardedRef.current = true;
    if (autoSaveRef.current) clearTimeout(autoSaveRef.current);
    onCancel();
  };

  // iter595 — Save-before-navigate: when the component unmounts (e.g. the
  // wizard step changes or the user navigates via the sidebar) and there are
  // valid lines that have NOT yet been persisted, fire a best-effort
  // fire-and-forget save. This closes the 0–1.5 s window between the user
  // entering a qty and the debounced autosave firing.
  // iter596c — Before firing, persist the draft to sessionStorage. If the
  // API call fails, the draft survives and is restored on next mount.
  useEffect(() => {
    return () => {
      if (hasSaved.current || discardedRef.current) return;
      const currentValidLines = linesRef.current.filter(
        l => l.beverage_product_id && parseFloat(l.quantity) > 0
      );
      if (currentValidLines.length === 0 || !targetLocIdRef.current) return;
      if (autoSaveRef.current) clearTimeout(autoSaveRef.current);
      // Synchronously persist draft before the async network call.
      const draft = {
        targetLocId: targetLocIdRef.current,
        date: dateRef.current,
        ref: refValueRef.current,
        lines: currentValidLines,
      };
      try { sessionStorage.setItem(draftKey, JSON.stringify(draft)); } catch { /* ignore quota */ }
      // Fire-and-forget — no await, no state update (component is unmounting).
      multiLocationService.createLocationTransfer({
        parent_session_id: parentSessionId,
        from_location_id: fromLocationId || parseInt(targetLocIdRef.current),
        to_location_id: toLocationId || parseInt(targetLocIdRef.current),
        transfer_date: dateRef.current,
        reference_number: refValueRef.current || null,
        lines: currentValidLines.map(l => ({
          beverage_product_id: parseInt(l.beverage_product_id),
          quantity: parseFloat(l.quantity),
          unit_label: l.unit_label || null,
          cost_per_unit: l.cost_per_unit ? parseFloat(l.cost_per_unit) : null,
        })),
      }).then(() => {
        // API succeeded — draft no longer needed.
        try { sessionStorage.removeItem(draftKey); } catch { /* ignore */ }
      }).catch(() => {
        // API failed — draft stays in sessionStorage; restored on next mount.
      });
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Build per-line unit options from the product
  const getUnitOptions = (productId) => {
    const prod = productMap[parseInt(productId)];
    if (!prod?.units?.length) return [{ value: 'unit', label: 'Unit' }];
    return prod.units.map(u => {
      let label = u.unit_type.charAt(0).toUpperCase() + u.unit_type.slice(1);
      const conv = parseFloat(u.conversion_to_base);
      const base = (u.base_unit || '').toLowerCase();
      // A size is shown only when it is a real measured size; count-only units (1 each, size unknown) show none.
      if (conv && u.unit_type !== 'case') {
        if (base && base !== 'ml') label += ` (${conv} ${base})`;
        else if (conv > 1 && !/\d\s*ml\b/i.test(u.unit_type)) label += ` (${conv}ml)`;
      }
      return { value: u.unit_type, label };
    });
  };

  return (
    <div className="border-2 border-dashed border-indigo-200 rounded-lg p-3 bg-indigo-50/30 space-y-2" data-testid="inline-transfer-entry">
      <div className="grid grid-cols-3 gap-2">
        <div className="space-y-1">
          <Label className="text-xs">{directionLabel}</Label>
          <Select value={targetLocId} onValueChange={setTargetLocId}>
            <SelectTrigger className="h-8 text-sm" data-testid="transfer-target-location"><SelectValue placeholder="Select..." /></SelectTrigger>
            <SelectContent>
              {locations.map(l => <SelectItem key={l.id} value={String(l.id)}>{l.name}{l.type === 'cellar' ? ' (Cellar)' : ''}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Date</Label>
          <Input type="date" value={date} min={periodFrom || undefined} max={periodTo || undefined} onChange={e => setDate(e.target.value)} className="h-8 text-sm" data-testid="transfer-date-input" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Reference</Label>
          <Input placeholder="Optional" value={ref} onChange={e => setRef(e.target.value)} className="h-8 text-sm" />
        </div>
      </div>
      <div className="text-[10px] text-muted-foreground flex gap-2 px-1">
        <div className="flex-[3]">Product</div>
        <div className="w-16">Qty</div>
        <div className="w-28">Unit</div>
        <div className="w-20">Cost/Unit</div>
        <div className="w-7"></div>
      </div>
      {lines.map((line, idx) => {
        const unitOpts = getUnitOptions(line.beverage_product_id);
        const lineValue = (parseFloat(line.quantity) || 0) * (parseFloat(line.cost_per_unit) || 0);
        return (
          <div key={line._key || idx} className="flex gap-2 items-start" data-testid={`inline-line-${idx}`}>
            <div className="flex-[3]">
              <ProductPicker products={products} value={line.beverage_product_id} onChange={v => handleProductChange(idx, v)} placeholder="Product..." testId={`il-product-${idx}`} />
            </div>
            <Input type="number" step="0.01" min="0" className="h-9 w-16 text-sm" placeholder="Qty" value={line.quantity} onChange={e => updateLine(idx, 'quantity', e.target.value)} data-testid={`il-qty-${idx}`} />
            <Select value={line.unit_label || 'unit'} onValueChange={v => handleUnitChange(idx, v)}>
              <SelectTrigger className="h-9 w-28 text-xs" data-testid={`il-unit-${idx}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                {unitOpts.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="w-20 h-9 flex items-center text-xs tabular-nums text-muted-foreground" title="Auto-calculated standard cost">
              {line.cost_per_unit ? fmtCurrency(parseFloat(line.cost_per_unit)) : '-'}
              {lineValue > 0 && <span className="ml-1 text-[9px]">= {fmtCurrency(lineValue)}</span>}
            </div>
            <Button size="icon" variant="ghost" className="h-9 w-7 text-red-400 shrink-0" onClick={() => removeLine(idx)}><Trash2 className="h-3 w-3" /></Button>
          </div>
        );
      })}
      <div className="flex items-center justify-between pt-1">
        <Button size="sm" variant="outline" onClick={addLine} data-testid="add-inline-line"><Plus className="h-3 w-3 mr-1" />Add Line</Button>
        <div className="flex items-center gap-2">
          {saving && <span className="text-xs text-muted-foreground flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" />Saving...</span>}
          <Button size="sm" variant="ghost" className="text-xs text-muted-foreground" onClick={handleDiscard} data-testid="discard-transfer">Discard</Button>
        </div>
      </div>
    </div>
  );
}


// ── iter368 — #6 Issues-to-Locations calculated grid ───────────────
//
// For each product in the cellar's count sheet the backend computes
//   calculated_issue = opening + deliveries − closing
// (all in stock units). The grid then auto-assigns this quantity to
// the single non-cellar location, or — when there are multiple
// non-cellar locations — lets the auditor enter a split per location
// while a per-row "remaining" cell counts the unallocated balance
// down to zero.
//
// Save → POST /sessions/:id/issues-to-locations/bulk-allocate
//   which replaces any prior AUTO-ISSUES transfers idempotently.
//
// 2026-11-04 fix — the separate "Save allocations" button is removed.
// The wizard's "Save & Next" now drives this step directly through the
// exposed `save()` ref: it validates (transfer date required), saves
// the grid, and issues the stock to locations in that one call — the
// auditor never has to click a second button. If it fails, `save()`
// throws (after toasting a clear error) so the caller stays on this
// step instead of silently advancing. The backend endpoint is already
// idempotent (delete-then-recreate by reference prefix) and
// concurrency-locked (`lock_parent`), so a double-click or a retry
// after a flaky network error reproduces the exact same transfers —
// no duplicate issues, stock movements or WAC drift. A `savingRef`
// guard additionally blocks a second call from this same component
// instance while one is already in flight.
export const IssuesToLocationsGrid = forwardRef(function IssuesToLocationsGrid({ parentSessionId, periodFrom, periodTo, onSaved }, ref) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  // allocations[pid][locationId] = string (so '' shows as blank input)
  const [allocations, setAllocations] = useState({});
  const [transferDate, setTransferDate] = useState(periodTo || '');
  const [search, setSearch] = useState('');
  // Reentry guard: Save & Next can call save() while the stale-banner's
  // own resave is still in flight (or a double-click on either trigger)
  // — block a second concurrent POST rather than racing the backend's
  // own delete-then-recreate against itself from two directions.
  const savingRef = useRef(false);

  const load = useCallback(async () => {
    if (!parentSessionId) return;
    setLoading(true);
    try {
      const res = await multiLocationService.getIssueSuggestions(parentSessionId);
      setData(res);
      // Seed allocations from current_allocations OR auto-assign for single-location case.
      // iter438b — when the response is flagged `is_stale` (cellar counts
      // were edited after the saved AUTO-ISSUES post), DO NOT preload the
      // stale values into the inputs. They no longer reflect reality and
      // were confusing auditors into approving wrong numbers ("the system
      // recommended 1416 bottles!"). Seed from the freshly-computed
      // `calculated_issue` against the auto-assigned location instead —
      // exactly what "Re-save with latest figures" would produce.
      const seed = {};
      const auto = res.auto_assign_to_location_id;
      const useFresh = !!res.is_stale;
      (res.products || []).forEach(p => {
        seed[p.beverage_product_id] = {};
        const cur = p.current_allocations || {};
        if (!useFresh && Object.keys(cur).length > 0) {
          Object.entries(cur).forEach(([locId, qty]) => {
            seed[p.beverage_product_id][locId] = String(qty);
          });
        } else if (auto && p.calculated_issue > 0) {
          seed[p.beverage_product_id][String(auto)] = String(p.calculated_issue);
        }
      });
      setAllocations(seed);
    } catch (err) {
      toast({ title: 'Failed to load issue suggestions', description: getApiError(err, 'Please try again'), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [parentSessionId, toast]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (periodTo && !transferDate) setTransferDate(periodTo); }, [periodTo, transferDate]);

  const handleAllocChange = (pid, locId, val) => {
    setAllocations(prev => ({
      ...prev,
      [pid]: { ...(prev[pid] || {}), [locId]: val },
    }));
  };

  const computeRemaining = (product) => {
    const allocs = allocations[product.beverage_product_id] || {};
    const sum = Object.values(allocs).reduce((s, v) => s + (parseFloat(v) || 0), 0);
    return Math.round((product.calculated_issue - sum) * 10000) / 10000;
  };

  const allFullyAllocated = useMemo(() => {
    if (!data) return false;
    return (data.products || []).every(p => Math.abs(computeRemaining(p)) < 0.0001 || p.calculated_issue <= 0);
  }, [data, allocations]);  // eslint-disable-line react-hooks/exhaustive-deps

  const handleApplyAuto = () => {
    if (!data?.auto_assign_to_location_id) return;
    const auto = String(data.auto_assign_to_location_id);
    const next = { ...allocations };
    (data.products || []).forEach(p => {
      if (p.calculated_issue > 0) {
        next[p.beverage_product_id] = { [auto]: String(p.calculated_issue) };
      }
    });
    setAllocations(next);
  };

  const handleSave = async () => {
    if (savingRef.current) {
      // A save is already in flight (double-click, or Save & Next fired
      // while the stale-banner resave was still running) — wait for it
      // instead of racing a second identical POST.
      while (savingRef.current) {
        await new Promise(r => setTimeout(r, 100));
      }
      return;
    }
    if (!transferDate) {
      toast({ title: 'Pick a transfer date', variant: 'destructive' });
      throw new Error('Transfer date is required before saving issues to locations');
    }
    const products = (data?.products || [])
      .map(p => ({
        beverage_product_id: p.beverage_product_id,
        unit_label: p.stock_unit_label,
        location_allocations: Object.entries(allocations[p.beverage_product_id] || {})
          .map(([locId, qty]) => ({ location_id: Number(locId), quantity: parseFloat(qty) || 0 }))
          .filter(la => la.quantity > 0),
      }))
      .filter(p => p.location_allocations.length > 0);
    savingRef.current = true;
    setSaving(true);
    try {
      await multiLocationService.bulkAllocateIssuesToLocations(parentSessionId, {
        transfer_date: transferDate,
        products,
      });
      if (products.length > 0) {
        toast({ title: 'Issues allocated', description: `${products.length} product line${products.length !== 1 ? 's' : ''} issued to locations.` });
      }
      if (onSaved) onSaved();
      await load();
    } catch (err) {
      toast({ title: 'Could not save issues to locations', description: getApiError(err, 'Please try again'), variant: 'destructive' });
      throw err;
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  // 2026-11-04 fix — exposed to the wizard's step navigation so "Save &
  // Next" can validate + save + issue in one call instead of requiring
  // a separate "Save allocations" click. No dependency array: always
  // points at the handleSave closure from THIS render, so it never
  // saves against stale `data`/`allocations`/`transferDate`.
  useImperativeHandle(ref, () => ({ save: handleSave }));

  if (loading) {
    return <Card><CardContent className="py-6 text-center text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Computing issues…</CardContent></Card>;
  }
  if (!data) return null;

  const locs = data.non_cellar_locations || [];
  const isAuto = !!data.auto_assign_to_location_id;
  const products = (data.products || []).filter(p => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (p.product_name || '').toLowerCase().includes(q) || (p.product_code || '').toLowerCase().includes(q);
  });
  const productsToShow = products.filter(p =>
    p.calculated_issue > 0
    || p.opening_qty > 0
    || p.deliveries_qty > 0
    || p.closing_qty > 0
    || Object.keys(allocations[p.beverage_product_id] || {}).length > 0
  );

  return (
    <Card data-testid="issues-to-locations-grid">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="text-sm">Calculated Issues to Locations</CardTitle>
            <CardDescription className="text-xs">
              Issue = Opening + Deliveries − Closing, per product (stock units).
              {isAuto && ` Auto-assigning to ${locs[0]?.location_name}.`}
              {!isAuto && ` Split across ${locs.length} locations — each row's "remaining" should reach zero.`}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Input type="date" value={transferDate} onChange={e => setTransferDate(e.target.value)} className="h-8 w-36 text-xs"
              min={periodFrom} max={periodTo} data-testid="issues-transfer-date" />
            {!isAuto && locs.length === 1 && (
              <Button size="sm" variant="outline" onClick={handleApplyAuto} data-testid="issues-apply-auto" title="Auto-fill all rows with the calculated quantity">
                Apply auto-assign
              </Button>
            )}
            {saving && (
              <span className="text-[11px] text-muted-foreground flex items-center gap-1" data-testid="issues-saving-indicator">
                <Loader2 className="h-3 w-3 animate-spin" />Saving…
              </span>
            )}
          </div>
        </div>
        {/* 2026-11-04 fix — the separate "Save allocations" button is
            removed: allocations are now saved and issued automatically
            when the auditor clicks "Save & Next" at the bottom of the
            wizard step (see `useImperativeHandle` above). Edits here
            just update local state until then. */}
        <p className="text-[11px] text-muted-foreground mt-1">
          Edit the grid below, then use <strong>Save &amp; Next</strong> to save and issue these quantities to the selected locations.
        </p>
      </CardHeader>
      <CardContent>
        {/* iter438 — out-of-date banner. Lights up when cellar counts
            were edited after the most-recent AUTO-ISSUES post, so the
            currently-saved transfers no longer reflect the cellar's
            true closing stock. One click re-saves the grid. */}
        {data.is_stale && (
          <div
            className="mb-3 flex items-start gap-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2"
            data-testid="issues-stale-banner"
          >
            <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
            <div className="flex-1 text-xs text-amber-900">
              <span className="font-semibold">Cellar counts have changed since the last allocation.</span>{' '}
              The previously-saved transfers are out of date — the grid below has been
              <strong> pre-filled with fresh figures</strong> from the latest cellar count.
              Review and click <em>Re-save with latest figures</em> to commit.
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => { handleSave().catch(() => { /* already toasted */ }); }}
              disabled={saving || !transferDate}
              data-testid="issues-stale-resave-btn"
              className="border-amber-400 bg-white hover:bg-amber-100"
            >
              {saving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
              Re-save with latest figures
            </Button>
          </div>
        )}
        <div className="flex items-center gap-2 mb-2">
          <Input placeholder="Search product…" value={search} onChange={e => setSearch(e.target.value)} className="h-8 text-xs max-w-xs" data-testid="issues-grid-search" />
          <span className="text-[11px] text-muted-foreground">
            {productsToShow.length} of {(data.products || []).length} products with non-zero issues
            {allFullyAllocated && <span className="ml-2 inline-flex items-center gap-1 text-emerald-600 font-medium">✓ all allocated</span>}
          </span>
        </div>
        <div className="overflow-x-auto border rounded-lg">
          <table className="w-full text-xs" data-testid="issues-grid-table">
            <thead className="bg-slate-50 sticky top-0">
              <tr>
                <th className="text-left px-2 py-2 font-semibold">Product</th>
                <th className="text-right px-2 py-2 font-semibold whitespace-nowrap">Opening</th>
                <th className="text-right px-2 py-2 font-semibold whitespace-nowrap">+ Deliveries</th>
                <th className="text-right px-2 py-2 font-semibold whitespace-nowrap">− Closing</th>
                <th className="text-right px-2 py-2 font-semibold whitespace-nowrap bg-amber-50">= Issue</th>
                {locs.map(l => (
                  <th key={l.location_id} className="text-right px-2 py-2 font-semibold whitespace-nowrap">{l.location_name}</th>
                ))}
                <th className="text-right px-2 py-2 font-semibold whitespace-nowrap bg-rose-50">Remaining</th>
              </tr>
            </thead>
            <tbody>
              {productsToShow.length === 0 ? (
                <tr><td colSpan={6 + locs.length} className="text-center py-4 text-muted-foreground">No products with calculated issues.</td></tr>
              ) : productsToShow.map(p => {
                const rem = computeRemaining(p);
                const isAllocated = Math.abs(rem) < 0.0001;
                return (
                  <tr key={p.beverage_product_id} className="border-t hover:bg-slate-50/40" data-testid={`issue-row-${p.beverage_product_id}`}>
                    <td className="px-2 py-1.5">
                      <div className="font-medium">{p.product_name}</div>
                      <div className="text-[10px] text-muted-foreground">{p.product_code} · {p.stock_unit_label}</div>
                    </td>
                    <td className="text-right px-2 tabular-nums">{p.opening_qty}</td>
                    <td className="text-right px-2 tabular-nums">{p.deliveries_qty}</td>
                    <td className="text-right px-2 tabular-nums">{p.closing_qty}</td>
                    <td className="text-right px-2 tabular-nums font-semibold bg-amber-50/40">{p.calculated_issue}</td>
                    {locs.map(l => (
                      <td key={l.location_id} className="px-1">
                        <Input
                          type="number"
                          inputMode="decimal"
                          min="0"
                          step="0.01"
                          value={allocations[p.beverage_product_id]?.[String(l.location_id)] || ''}
                          onChange={e => handleAllocChange(p.beverage_product_id, String(l.location_id), e.target.value)}
                          className="h-7 text-right text-xs px-1 tabular-nums w-20 ml-auto"
                          placeholder="0"
                          disabled={isAuto && l.location_id !== data.auto_assign_to_location_id}
                          data-testid={`issue-alloc-${p.beverage_product_id}-${l.location_id}`}
                        />
                      </td>
                    ))}
                    <td className={`text-right px-2 tabular-nums font-semibold ${isAllocated ? 'text-emerald-600' : (rem > 0 ? 'text-rose-600' : 'text-amber-600')}`} data-testid={`issue-remaining-${p.beverage_product_id}`}>
                      {isAllocated ? '0' : rem}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
});
